/**
 * Maps Sendbird message/channel data to app shapes.
 */
import type { GroupChannel } from '@sendbird/chat/groupChannel';
import type { BaseMessage } from '@sendbird/chat/message';
import { getOtherUserInformation } from '@/apps/messages/helpers/otherUserInfo';
import type { OtherUserInfo } from '@/apps/messages/helpers/otherUserInfo';
import {
  getChannelPreviewImage,
  getChannelPreviewName,
  getLastMessageContent,
  getLastMessageDate,
  formatMessageTime,
} from '@/apps/messages/helpers/channelPreview';
import { formatUnparsedAdminMessageBody, parseAdminMessage } from '@/apps/messages/helpers/adminMessageFormatter';
import type { ChannelListItemData, MessageItemData, MessageStatus } from '@/apps/messages/types';

interface ChannelLastMessage {
  message?: string;
  createdAt?: number;
}

interface ChannelMember {
  userId?: string;
  nickname?: string;
  plainProfileUrl?: string;
  metaData?: Record<string, string>;
}

const getChannelMembers = (channel: GroupChannel): ChannelMember[] => channel.members as ChannelMember[];

export const mapChannelToPreview = (channel: GroupChannel, currentUserId?: string): ChannelListItemData => {
  const otherUserInfo = getOtherUserInformation(getChannelMembers(channel), currentUserId);
  const lastMessage = channel.lastMessage as ChannelLastMessage | null;

  return {
    url: channel.url,
    name: getChannelPreviewName(otherUserInfo),
    avatarUrl: getChannelPreviewImage(otherUserInfo),
    lastMessage: getLastMessageContent(otherUserInfo, lastMessage || undefined),
    lastMessageAt: getLastMessageDate(otherUserInfo, lastMessage || undefined),
    unreadCount: channel.unreadMessageCount || 0,
    otherUser: otherUserInfo,
  };
};

interface MessageSender {
  userId?: string;
  nickname?: string;
  plainProfileUrl?: string;
}

type MessageWithSender = BaseMessage & {
  sender?: MessageSender;
  messageType?: string;
};

/**
 * Calculate read status for an outgoing message.
 * Only checks read status for the most recent message to avoid expensive calculations per message.
 */
const calculateReadStatus = (
  message: BaseMessage,
  isOutgoing: boolean,
  isLastMessage: boolean,
  channel?: GroupChannel
): boolean => {
  // Only calculate read status for outgoing messages and only for the most recent one
  if (!isOutgoing || !isLastMessage || !channel) {
    return false;
  }

  try {
    // Get unread members (excluding current user)
    const unreadMembers = channel.getUnreadMembers(message, false);
    // If there are no unread members, the message has been read by all other users
    return unreadMembers.length === 0;
  } catch (_error) {
    // If there's an error (e.g., message not found), default to false
    return false;
  }
};

/**
 * Determine the message status for display (sent, delivered, or read).
 * Only applies to outgoing messages.
 */
const calculateMessageStatus = (message: BaseMessage, isOutgoing: boolean, read: boolean): MessageStatus => {
  // Status indicators only apply to outgoing messages
  if (!isOutgoing) {
    return 'delivered'; // Default for incoming messages
  }

  // If read by all recipients, status is read
  if (read) {
    return 'read';
  }

  // If message has a messageId (confirmed sent to Sendbird), status is delivered
  // Otherwise it's optimistic/pending, status is sent
  if (message.messageId && typeof message.messageId === 'number' && message.messageId > 0) {
    return 'delivered';
  }

  return 'sent';
};

export const mapMessageToItem = (
  message: BaseMessage,
  currentUserId: string | undefined,
  otherUserInfo: OtherUserInfo,
  accountType: 'parent' | 'sitter',
  channel?: GroupChannel,
  isLastMessage: boolean = false
): MessageItemData => {
  const withSender = message as MessageWithSender;
  const senderSendbirdId = (message as any).sender?.userId;
  const senderName = withSender.sender?.nickname || otherUserInfo.name || '';
  const senderAvatarUrl = withSender.sender?.plainProfileUrl || otherUserInfo.image || '';

  // Sendbird is connected with userId = Sittercity account ID (see sendbirdClient), so sender.userId equals currentUserId for own messages.
  const isOutgoing = senderSendbirdId != null && currentUserId != null && senderSendbirdId === currentUserId;

  const isAdmin = withSender.messageType === 'admin';
  const adminMessageData = isAdmin ? parseAdminMessage(withSender, otherUserInfo, accountType) : null;

  const rawBody = withSender.message || '';
  const currentUserNickname = channel?.members?.find((m) => (m as ChannelMember).userId === currentUserId)?.nickname || '';
  const body =
    isAdmin && !adminMessageData
      ? formatUnparsedAdminMessageBody(rawBody, {
          accountType,
          otherUserName: otherUserInfo.name || '',
          currentUserNickname,
        })
      : rawBody;

  const { createdAt } = message as { createdAt?: number };

  // Calculate read status only for the most recent outgoing message
  const read = calculateReadStatus(message, isOutgoing, isLastMessage, channel);

  // Calculate message status (sent, delivered, or read)
  const messageStatus = calculateMessageStatus(message, isOutgoing, read);

  return {
    id: message.messageId,
    body,
    adminMessageData,
    senderName,
    senderAvatarUrl,
    isOutgoing,
    isAdmin,
    timestamp: createdAt ? formatMessageTime(createdAt) : '',
    createdAt,
    read,
    messageStatus,
    accountType,
  };
};
